import { useMemo } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";
import { useFragment } from "react-relay";
import Slider from "react-slick";
import { graphql } from "relay-runtime";

import { partnerTemplateCardCarouselContentfulRelayFragment$key } from "@/__generated_contentful__/partnerTemplateCardCarouselContentfulRelayFragment.graphql";
import { ConfluenceTemplateMessages } from "@/components/confluence/templates/template-messages";
import { ConfluenceTemplateAnalytics } from "@/components/confluence/templates/util/confluence-templates-analytics";
import { partnerTemplatesDataUrlSlugs } from "@/templates/software/confluence/templates/confluence-templates-page/src/test-data/test-data";

import { PartnerTemplateCard } from "./partner-template-card-contentful-relay";

import type { Settings as SlickSettings } from "react-slick";

// Source https://github.com/kenwheeler/slick/blob/master/slick/slick.css
const slickStyle = css({
    // nested selector for third-party component
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-slider": {
        position: "relative",
        display: "block",
        boxSizing: "border-box",
        WebkitUserSelect: "none",
        MozUserSelect: "none",
        MsUserSelect: "none",
        userSelect: "none",
        WebkitTouchCallout: "none",
        KhtmlUserSelect: "none",
        MsTouchAction: "pan-y",
        touchAction: "pan-y",
        WebkitTapHighlightColor: "transparent"
    },
    // nested selector for third-party component
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-list": {
        position: "relative",
        display: "block",
        overflow: "hidden",
        margin: 0,
        padding: 0
    },
    // nested selector for third-party component
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-list:focus": {
        outline: "none"
    },
    // nested selector for third-party component
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-list.dragging": {
        cursor: "pointer"
    },
    // nested selector for third-party component
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-slider .slick-track, .slick-slider .slick-list": {
        WebkitTransform: "translate3d(0, 0, 0)",
        MozTransform: "translate3d(0, 0, 0)",
        MsTransform: "translate3d(0, 0, 0)",
        OTransform: "translate3d(0, 0, 0)",
        transform: "translate3d(0, 0, 0)"
    },
    // nested selector for third-party component
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-track": {
        position: "relative",
        insetBlockStart: 0,
        insetInlineStart: 0,
        display: "block",
        marginInlineStart: "auto",
        marginInlineEnd: "auto"
    },
    // nested selector for third-party component
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-track:before, .slick-track:after": {
        display: "table",
        content: "''"
    },
    // nested selector for third-party component
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-track:after": {
        clear: "both"
    },
    // nested selector for third-party component
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-loading .slick-track": {
        visibility: "hidden"
    },
    // nested selector for third-party component
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-slide": {
        display: "none",
        float: "left",
        height: "100%",
        minHeight: "1px"
    },
    // nested selector for third-party component
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "[dir='rtl'] .slick-slide": {
        float: "right"
    },
    // nested selector for third-party component
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-slide img": {
        display: "block"
    },
    // nested selector for third-party component
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-slide.slick-loading img": {
        display: "none"
    },
    // nested selector for third-party component
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-slide.dragging img": {
        pointerEvents: "none"
    },
    // nested selector for third-party component
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-initialized .slick-slide": {
        display: "block"
    },
    // nested selector for third-party component
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-loading .slick-slide": {
        visibility: "hidden"
    },
    // nested selector for third-party component
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-vertical .slick-slide": {
        display: "block",
        height: "auto",
        border: "1px solid transparent"
    },
    // nested selector for third-party component
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-arrow.slick-hidden": {
        display: "none"
    }
});

const carouselContainer = css({
    "position": "relative",
    "width": "960px",
    "margin": "0 auto",
    "@media (max-width: 539.99px)": {
        width: "328px",
        height: "480px"
    },
    "@media (min-width: 540px) and (max-width: 1023.99px)": {
        width: "592px",
        margin: "0 auto"
    },
    "@media (min-width: 1024px) and (max-width: 1279.99px)": {
        width: "900px"
    }
});
const carouselCardWrapper = css({
    display: "inline-block",
    width: "100%"
});

const buttonLeft = css({
    "display": "block",
    "position": "absolute",
    "insetBlockStart": "50%",
    "width": 32,
    "height": 32,
    "border": "0 none",
    "transform": "translateY(-60%)",
    "borderRadius": "50%",
    "outline": "none",
    "cursor": "pointer",
    "color": "#42526e",
    "lineHeight": 28,
    "insetInlineStart": -72,
    "backgroundColor": "transparent",

    "@media (max-width: 1023.99px)": {
        insetInlineStart: 0,
        zIndex: 1,
        // eslint-disable-next-line local/ensure-wac-token-usage
        backgroundColor: "#ffffff",
        // eslint-disable-next-line local/ensure-wac-token-usage
        boxShadow: "1px 1px 6px 0 rgba(0,0,0,.2)"
    }
});

const buttonRight = css({
    "display": "block",
    "position": "absolute",
    "insetBlockStart": "50%",
    "width": 32,
    "height": 32,
    "border": "0 none",
    "transform": "translateY(-60%)",
    "borderRadius": "50%",
    "outline": "none",
    "cursor": "pointer",

    "color": "#42526e",
    "lineHeight": 28,
    "insetInlineEnd": -72,
    "backgroundColor": "transparent",
    "@media (max-width: 1023.99px)": {
        insetInlineEnd: 0,
        zIndex: 1,
        // eslint-disable-next-line local/ensure-wac-token-usage
        backgroundColor: "#ffffff",
        // eslint-disable-next-line local/ensure-wac-token-usage
        boxShadow: "1px 1px 6px 0 rgba(0,0,0,.2)"
    }
});

const buttonLeftArrow = css({
    transform: "rotate(-45deg)",
    width: "12px",
    height: "12px",
    lineHeight: "0px",
    position: "absolute",
    marginBlockStart: "-6px",
    borderBlockStart: "3px solid",
    borderInlineStart: "3px solid",
    borderBlockLeft: "3px solid",
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#42526e",
    fontSize: "16px"
});

const buttonRightArrow = css({
    transform: "rotate(135deg)",
    width: "12px",
    height: "12px",
    lineHeight: "0px",
    position: "absolute",
    marginBlockStart: "-6px",
    borderBlockStart: "3px solid",
    borderInlineStart: "3px solid",
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#42526e",
    fontSize: "16px"
});

const buttonRoundedArrow = css({
    width: "12px",
    height: "12px",
    display: "inline-block",
    lineHeight: "1px",
    position: "absolute",
    marginBlockStart: "-6px",
    boxSizing: "border-box",
    borderBlockStart: "3px solid",
    borderInlineStart: "3px solid",
    borderBlockLeft: "3px solid",
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#0052cc",
    fontSize: "16px"
});

const buttonRoundedLeftArrow = css({
    transform: "rotate(-45deg)",
    marginInlineStart: -4
});

const buttonRoundedRightArrow = css({
    transform: "rotate(135deg)",
    marginInlineStart: -8
});

const PartnerTemplateCardCarouselFragment = graphql`
    fragment partnerTemplateCardCarouselContentfulRelayFragment on ProductTemplate
    @relay(plural: true) {
        sys {
            id
        }
        urlSlug
        ...partnerTemplateCardContentfulRelayFragment
    }
`;

export type PartnerTemplateCarouselProps = {
    templates: partnerTemplateCardCarouselContentfulRelayFragment$key;
};

export const PartnerTemplateCardCarousel = ({
    templates
}: PartnerTemplateCarouselProps) => {
    const data = useFragment(PartnerTemplateCardCarouselFragment, templates);

    const partnerTemplates = useMemo(
        () =>
            data
                .map((item) => item)
                .sort(
                    (a, b) =>
                        partnerTemplatesDataUrlSlugs.indexOf(a.urlSlug ?? "") -
                        partnerTemplatesDataUrlSlugs.indexOf(b.urlSlug ?? "")
                ),
        [data]
    );

    const intl = useIntl();
    const previousLabel = intl.formatMessage(
        ConfluenceTemplateMessages.carouselPreviousLabel
    );
    const nextLabel = intl.formatMessage(
        ConfluenceTemplateMessages.carouselNextLabel
    );

    const LeftArrow = ({ onClick }: { onClick?: () => void }) => (
        <button
            css={buttonLeft}
            onClick={onClick}
            className="doNotAutoTrack"
            aria-label={previousLabel}
        >
            <span css={buttonLeftArrow} />
        </button>
    );
    const RightArrow = ({ onClick }: { onClick?: () => void }) => (
        <button
            css={buttonRight}
            onClick={onClick}
            className="doNotAutoTrack"
            aria-label={nextLabel}
        >
            <span css={buttonRightArrow} />
        </button>
    );

    const LeftArrowRounded = ({ onClick }: { onClick?: () => void }) => (
        <button
            css={buttonLeft}
            onClick={onClick}
            className="doNotAutoTrack"
            aria-label={previousLabel}
        >
            <span css={[buttonRoundedArrow, buttonRoundedLeftArrow]} />
        </button>
    );

    const RightArrowRounded = ({ onClick }: { onClick?: () => void }) => (
        <button
            css={buttonRight}
            onClick={onClick}
            className="doNotAutoTrack"
            aria-label={nextLabel}
        >
            <span css={[buttonRoundedArrow, buttonRoundedRightArrow]} />
        </button>
    );

    const settings: SlickSettings = {
        prevArrow: <LeftArrow />,
        nextArrow: <RightArrow />,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    prevArrow: <LeftArrowRounded />,
                    nextArrow: <RightArrowRounded />
                }
            }
        ]
    };
    const beforeChange = (currentSlide: number, nextSlide: number) => {
        ConfluenceTemplateAnalytics.sendPartnerCarouselAnalytic(
            currentSlide,
            nextSlide,
            partnerTemplates.length
        );
    };

    return (
        <div css={carouselContainer}>
            <div css={slickStyle}>
                <Slider {...settings} beforeChange={beforeChange}>
                    {partnerTemplates.map((partnerTemplate) => (
                        <div
                            css={carouselCardWrapper}
                            key={partnerTemplate?.sys?.id}
                        >
                            <PartnerTemplateCard template={partnerTemplate} />
                        </div>
                    ))}
                </Slider>
            </div>
        </div>
    );
};
